
import { createContext, useState, useContext, useEffect, ReactNode } from "react";
import { Policy, Acknowledgment, PolicyReview } from "@/types";
import { useAuth } from "../AuthContext";
import { PolicyContextType } from "./types";
import { samplePolicies } from "./sampleData";
import { usePolicyStorage } from "./usePolicyStorage";
import { usePolicyOperations } from "./usePolicyOperations";

const PolicyContext = createContext<PolicyContextType | undefined>(undefined);

export const PolicyProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { policies, setPolicies, acknowledgments, setAcknowledgments, loadFromStorage, saveToStorage } = usePolicyStorage(samplePolicies);
  const { 
    addPolicy, 
    updatePolicy, 
    isUserPolicyOwner, 
    acknowledgePolicy, 
    isPolicyAcknowledged, 
    reviewPolicy,
    getPolicyVersionHistory,
    canUserViewDrafts,
    getDraftPolicies,
    getPublishedPolicies,
    publishDraftPolicy,
    getArchivedPolicies,
    archivePolicy,
    unarchivePolicy
  } = usePolicyOperations(user, policies, setPolicies, acknowledgments, setAcknowledgments, saveToStorage);
  
  // Load data from storage on mount
  useEffect(() => {
    loadFromStorage();
  }, []);

  return (
    <PolicyContext.Provider
      value={{
        policies,
        acknowledgments,
        addPolicy,
        acknowledgePolicy,
        isPolicyAcknowledged,
        reviewPolicy,
        updatePolicy,
        isUserPolicyOwner,
        getPolicyVersionHistory,
        canUserViewDrafts,
        getDraftPolicies,
        getPublishedPolicies,
        getArchivedPolicies,
        publishDraftPolicy,
        archivePolicy,
        unarchivePolicy
      }}
    >
      {children}
    </PolicyContext.Provider>
  );
};

export const usePolicy = () => {
  const context = useContext(PolicyContext);
  if (context === undefined) {
    throw new Error("usePolicy must be used within a PolicyProvider");
  }
  return context;
};
